import React, {
  FC,
  ReactNode,
  CSSProperties,
  useRef,
  useLayoutEffect,
} from 'react'
import styled, { keyframes, css } from 'styled-components'

import Popper from '../Popper'
import { Placement } from '@popperjs/core'
import { easings } from 'constants/easings'
import useHover from 'utils/hooks/useHover'

const reveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-5px)
  }
  100% {
    opacity: 1;
    transform: translateY(0)
  }
`

type TooltipContentProps = Pick<TooltipProps, 'scrollable' | 'placement'>

const TooltipContent = styled.div<TooltipContentProps>(
  ({
    theme: { colors, radii, fontWeights, transitions },
    scrollable,
    placement,
  }) => css`
    font-size: 0.6875rem;
    color: ${colors.text.light.base};
    background: ${colors.background.dark.base};
    filter: drop-shadow(0 4px 12px ${colors.border.dark.high});
    border-radius: ${radii.xxlarge};
    padding: 8px 12px;
    font-weight: ${fontWeights.medium};
    z-index: 9999;
    animation: ${reveal} ${transitions.duration[4]}
      ${easings.easeOutExponential};

    /* Forces hardware acceleration in Safari which fixes a persistent shadow bug*/
    transform: translate3d(0, 0, 0);

    /* This can be extended for every other tooltip position */
    ${scrollable && placement === 'top'
      ? css`
          &:after {
            content: ' ';
            position: absolute;
            bottom: -10px;
            width: 100%;
            height: 10px;
            left: 0;
          }
        `
      : ''}
  `
)

export interface TooltipChildrenProps {
  ref: React.Ref<any>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export interface TooltipProps {
  children: ((props: TooltipChildrenProps) => ReactNode) | ReactNode
  content: ReactNode
  /** Styles to apply to the tooltip container */
  contentStyle?: CSSProperties
  /** This prop is deprecated, but still here for backwards compatibily */
  arrowStyle?: CSSProperties
  style?: CSSProperties
  /** space between the tooltip and the trigger */
  spacing?: string
  /** data-testid attr of the container */
  'data-testid'?: string
  disabled?: boolean
  placement?: Placement
  className?: string
  scrollable?: boolean
  visible?: boolean
  ref?: null | HTMLDivElement
}

const TooltipTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.light.base};
`

const TooltipBody = styled.div`
  color: ${({ theme }) => theme.colors.foreground.primary[2]};
`

/**
 * Used for contextual help. For instance, when you need to provide
 * more context to a icon button on hover.
 *
 * It can have different placements, you can check all of them via
 * the props table on Storybook.
 */
const Tooltip: FC<TooltipProps> = ({
  children,
  className,
  content,
  disabled = false,
  scrollable = false,
  placement,
  ...props
}: TooltipProps) => {
  const [visible, hoverEventHandlers] = useHover()
  const forceUpdateRef = useRef<(() => void) | null>()

  useLayoutEffect(() => {
    // We need to call forceUpdate from Popper
    // to reposition the tooltip whenever the content
    // changes, avoiding misalignment.
    // https://github.com/sketch-hq/Cloud/issues/5334
    const forceUpdateFn = forceUpdateRef?.current
    if (forceUpdateFn) {
      forceUpdateFn()
    }
  }, [content])

  return (
    <Popper
      className={className}
      visible={visible && !disabled}
      placement={placement}
      spacing="5px"
      {...props}
      {...hoverEventHandlers}
      popup={({ forceUpdate, ...popperProps }) => {
        forceUpdateRef.current = forceUpdate

        return (
          <TooltipContent
            {...popperProps}
            {...(scrollable ? hoverEventHandlers : {})}
            scrollable={scrollable}
            placement={placement}
            data-testid="tooltip">
            {content}
          </TooltipContent>
        )
      }}>
      {children}
    </Popper>
  )
}

export default Object.assign(Tooltip, {
  Title: TooltipTitle,
  Body: TooltipBody,
})
